import React, { FunctionComponent } from 'react';
import './loader.css';
import { MdError, MdHistoryToggleOff } from 'react-icons/md';
import DisplayAreaCenteredWrapper from '../layout/utilities/displayAreaWrapper';
import RaptorLogoWithText from '../../images/RaptorLogoWithText.png';
import { mainColors } from '../../styling/theme';
import makeStyles from '@mui/styles/makeStyles';
import { cn } from '../../utilities/cn';
import GridItem from '../layout/GridComponents/GridItem';

interface Props {
  centerWrap?: boolean;
  setHeight?: number | string;
  setWidth?: number | string;
  message?: string;
  noMargin?: boolean;
  noMinHeightOnError?: boolean;
  children?: React.ReactNode;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.25rem',
    marginBottom: '6.25rem',
    maxWidth: '62.50rem',
    backgroundColor: 'white',
    padding: '3.12rem 3.75rem 1.25rem 3.75rem',
    borderRadius: '1.25rem',
  },
  message: {
    fontSize: '3.12rem',
    fontWeight: 500,
    color: mainColors.mainBlue,
    textAlign: 'center',
  },
});

function NoDataMessage({ children, ...props }: Props) {
  const classes = useStyles();

  const message = (
    <div className={classes.container}>
      <div>
        <img src={RaptorLogoWithText} alt={'Raptor Logo'} />
      </div>
      <h2 className={classes.message}>
        {props.message ? props.message : 'No Data Available'}
      </h2>
      {children}
    </div>
  );

  return (
    <DisplayAreaCenteredWrapper noMinHeight={props.noMinHeightOnError} setHeight={props.setHeight}>
      {message}
    </DisplayAreaCenteredWrapper>
  );
}

export default NoDataMessage;

interface ErrorDataMessageProps extends React.HTMLProps<HTMLDivElement> {
  message?: string;
  description?: string;
  children?: React.ReactNode;
}

export const ErrorDataMessage: FunctionComponent<ErrorDataMessageProps> = ({
  message = 'Error Retrieving Data',
  description = 'An error occurred, and a report has been submitted to the site administrator. Please try again later',
  children,
  ...props
}) => {
  return (
    <div
      id="alert-additional-content-2"
      className={cn(
        'mb-4 rounded-lg border border-red-300 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-gray-800 dark:text-red-400',
        props.className,
      )}
      role="alert"
    >
      <div className="flex items-center gap-3">
        <MdError />
        <span className="sr-only">Info</span>
        <h3 className="text-lg font-medium">{message}</h3>
      </div>
      <div className="mb-4 mt-2 text-sm">{description}</div>
      {children}
    </div>
  );
};

interface NoHistoricalDataMessageProps {
  message?: string;
  description?: string;
}

export const NoHistoricalDataMessage: React.FC<NoHistoricalDataMessageProps> = ({
  message = 'No Historical Data Available',
  description = 'No historical data is available for the selected period. Please try selecting a different date or check back later.',
}) => {
  return (
    <GridItem xs={12} className='my-4'>
      <div className="flex flex-col items-center justify-center gap-6 py-12 px-8 bg-white rounded-lg shadow min-h-[400px] w-full">
      {/* Message Section */}
      <div className="flex flex-col items-center gap-4">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-[#F3F9FF] text-mainblue">
          <MdHistoryToggleOff className='text-4xl' />
   
        </div>
        <h2 className="text-2xl font-semibold text-mainblue text-center m-0">
          {message}
        </h2>
        <p className="text-base text-[#A8A8A8] text-center max-w-[500px] leading-6">
          {description}
        </p>
        <div className="flex items-center gap-2 py-3 px-5 bg-[#F3F9FF] rounded-lg mt-2">
          <svg className="w-5 h-5 text-mainblue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm text-mainblue">
            Historical data will appear here once it becomes available
          </span>
        </div>
      </div>
    </div>
    </GridItem>
  );
};
